// packages/shared/src/team/zod.ts
//
// Team domain Zod schemas (P3.4a.).
//
// Node-pure (no Fastify / Mongoose). Safe to bundle in Vite client builds.
// Used by:
//   - apps/api/src/modules/team/handlers (request body validation)
//   - apps/{admin,merchant}/src/api (FE form validation)
//
// Three primary schemas exported:
//   - createTeamSchema  → POST /api/v1/teams body
//   - updateTeamSchema  → PATCH /api/v1/teams/:id body (all fields optional)
//   - teamRowSchema     → response/list row shape (full document projection)
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Primitive schemas (kept inline to avoid Wave-1 ordering deps; reviewer
// dedupes against ledger/types.ts post-wave).
// ---------------------------------------------------------------------------
/** MongoDB ObjectId hex string (24 lowercase hex chars). */
const ObjectIdHexSchema = z.string().regex(/^[a-f0-9]{24}$/, {
    message: 'Must be a 24-character lowercase hex ObjectId',
});
/** Unix-ms timestamp (non-negative integer). */
const UnixMsSchema = z.number().int().nonnegative();
// ---------------------------------------------------------------------------
// Enums (P3.4a.)
// ---------------------------------------------------------------------------
export const TeamStatusSchema = z.enum(['active', 'deactivated']);
export const SpendingLimitPeriodSchema = z.enum([
    'daily',
    'weekly',
    'biweekly',
    'monthly',
    'quarter',
]);
export const StoreAssignmentModeSchema = z.enum(['specific', 'all']);
// ---------------------------------------------------------------------------
// Sub-objects
// ---------------------------------------------------------------------------
/**
 * Store assignment payload.
 *
 * Cross-field invariant enforced via `.superRefine`:
 *   - mode = "all"      → storeIds MUST be empty (resolver expands at runtime)
 *   - mode = "specific" → storeIds MUST be non-empty
 */
export const StoreAssignmentSchema = z
    .object({
    mode: StoreAssignmentModeSchema,
    storeIds: z.array(ObjectIdHexSchema).default([]),
})
    .superRefine((val, ctx) => {
    if (val.mode === 'all' && val.storeIds.length > 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['storeIds'],
            message: 'storeIds must be empty when mode = "all" (resolver is dynamic)',
        });
    }
    if (val.mode === 'specific' && val.storeIds.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['storeIds'],
            message: 'storeIds must be non-empty when mode = "specific"',
        });
    }
});
/**
 * Spending-limit payload.
 *
 * Cross-field invariants enforced via `.superRefine`:
 *   - reset = false → totalCents required, periodCents/period MUST be omitted
 *   - reset = true  → periodCents + period required, totalCents MUST be omitted
 */
export const SpendingLimitSchema = z
    .object({
    totalCents: z.number().int().nonnegative().optional(),
    periodCents: z.number().int().nonnegative().optional(),
    period: SpendingLimitPeriodSchema.optional(),
    reset: z.boolean(),
})
    .superRefine((val, ctx) => {
    if (val.reset === false) {
        if (val.totalCents === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['totalCents'],
                message: 'totalCents is required when reset = false (total-cap mode)',
            });
        }
        if (val.periodCents !== undefined || val.period !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['periodCents'],
                message: 'periodCents/period must be omitted when reset = false (total-cap mode)',
            });
        }
    }
    else {
        if (val.periodCents === undefined || val.period === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['periodCents'],
                message: 'periodCents and period are required when reset = true (reset-per-period mode)',
            });
        }
        if (val.totalCents !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['totalCents'],
                message: 'totalCents must be omitted when reset = true (reset-per-period mode)',
            });
        }
    }
});
// ---------------------------------------------------------------------------
// Request bodies
// ---------------------------------------------------------------------------
/** Display-name constraints — keep terse but human-friendly. */
const TeamNameSchema = z
    .string()
    .trim()
    .min(1, 'Team name is required')
    .max(120, 'Team name must be 120 characters or fewer');
const TeamDescriptionSchema = z
    .string()
    .trim()
    .max(2000, 'Description must be 2000 characters or fewer');
/**
 * POST /api/v1/teams body schema.
 *
 * `merchantId` is derived server-side from the authenticated principal —
 * NOT accepted from the client. teamLeadUserId is required (P3.4a.).
 */
export const createTeamSchema = z.object({
    name: TeamNameSchema,
    description: TeamDescriptionSchema.optional(),
    storeAssignment: StoreAssignmentSchema,
    teamLeadUserId: ObjectIdHexSchema,
    spendingLimit: SpendingLimitSchema,
    status: TeamStatusSchema.default('active'),
});
/**
 * PATCH /api/v1/teams/:id body schema. All fields optional; handler enforces
 * "at least one provided" semantics.
 */
export const updateTeamSchema = z
    .object({
    name: TeamNameSchema.optional(),
    description: TeamDescriptionSchema.optional(),
    storeAssignment: StoreAssignmentSchema.optional(),
    teamLeadUserId: ObjectIdHexSchema.optional(),
    spendingLimit: SpendingLimitSchema.optional(),
    status: TeamStatusSchema.optional(),
})
    .refine((v) => Object.keys(v).length > 0, {
    message: 'At least one field must be provided',
});
// ---------------------------------------------------------------------------
// Response row
// ---------------------------------------------------------------------------
/**
 * Full Team document projection — used by GET /teams + GET /teams/:id +
 * list endpoints. Mirrors `ITeam` exactly.
 */
export const teamRowSchema = z.object({
    _id: ObjectIdHexSchema,
    merchantId: ObjectIdHexSchema,
    name: z.string(),
    description: z.string().optional(),
    storeAssignment: z.object({
        mode: StoreAssignmentModeSchema,
        storeIds: z.array(ObjectIdHexSchema),
    }),
    teamLeadUserId: ObjectIdHexSchema,
    status: TeamStatusSchema,
    spendingLimit: z.object({
        totalCents: z.number().int().nonnegative().optional(),
        periodCents: z.number().int().nonnegative().optional(),
        period: SpendingLimitPeriodSchema.optional(),
        reset: z.boolean(),
    }),
    createdAt: UnixMsSchema,
    updatedAt: UnixMsSchema,
    isDeleted: z.boolean(),
    deletedAt: UnixMsSchema.nullable(),
    createdBy: z.string().nullable(),
    updatedBy: z.string().nullable(),
});
//# sourceMappingURL=zod.js.map